import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/auth/SessionProvider'
import { FlowForm } from '@/auth/FlowForm'
import { flowDriver } from '@/auth/flows'
import type { AnyFlow } from '@/auth/flows'
import { stepUpRequired } from '@/auth/flow'
import { PrivacyConsents } from '@/privacy/PrivacyConsents'
import { Appearance } from '@/components/Appearance'
import './Settings.css'

type State =
  | { readonly kind: 'starting' }
  | { readonly kind: 'ready'; readonly flow: AnyFlow }
  | { readonly kind: 'unavailable'; readonly reason: string }

/**
 * Account settings — password, email, and whatever else the tenant enables.
 *
 * The same Ory flow renderer as sign-in, behind the session gate. Two things
 * differ from the unauthenticated flows and both matter:
 *
 *   1. **Success is not a navigation.** Login ends by issuing a session;
 *      settings ends with `200` and the *updated* flow carrying a confirmation
 *      message. Navigating away on success would discard Ory's confirmation and
 *      leave someone unsure whether their password actually changed.
 *   2. **Ory can demand a fresh session.** Changing a password on an old
 *      session returns `403 session_refresh_required`. That is a correct
 *      security control, not an error — the customer is sent to sign in again
 *      and returned here, rather than shown a raw failure.
 */
export function Settings() {
  const { ory, refresh } = useSession()
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ kind: 'starting' })
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const start = useCallback(async () => {
    setState({ kind: 'starting' })
    try {
      const driver = flowDriver(ory, 'settings')
      // RESUME the flow we were already in, when there is one (#1025).
      //
      // A settings flow carries the TOTP secret it minted. Creating a fresh one
      // mints a SECOND secret and throws the first away — so a customer who had
      // already scanned the code was left holding a secret Ory no longer knew,
      // and every code they typed was refused. They saw a new QR and no
      // explanation.
      //
      // It happens on an ordinary path, not an exotic one: Ory asks for a fresh
      // session part-way through enrolment, we send them to sign in, and the
      // return journey used to start again from nothing.
      //
      // A flow id that Ory will not return — expired, or belonging to somebody
      // else — falls through to `create`, because refusing here would strand
      // somebody on a stale link with no way forward.
      const resumeId = new URLSearchParams(location.search).get('flow')
      let flow: AnyFlow | null = null
      if (resumeId !== null && resumeId !== '' && driver.resume !== undefined) {
        try {
          flow = await driver.resume(resumeId)
        } catch {
          flow = null
        }
      }
      if (flow === null) flow = await driver.create()
      if (alive.current) setState({ kind: 'ready', flow })
    } catch (err) {
      if (!alive.current) return
      // Carry WHICH step-up Ory asked for, not merely that it asked (#47).
      //
      // `/sign-in` cannot work it out for itself: it would have to guess between
      // `refresh=true` and `aal=aal2`, and guessing wrong asks a customer for
      // the factor they have already given and refuses them again. Sending the
      // page here without the reason is how this became an unbounded loop.
      const stepUp = await stepUpRequired(err)
      if (stepUp !== null) {
        navigate('/sign-in', { replace: true, state: { from: '/settings', stepUp } })
        return
      }
      setState({
        kind: 'unavailable',
        reason: err instanceof Error ? err.message : String(err),
      })
    }
  }, [navigate, ory])

  useEffect(() => {
    void start()
  }, [start])

  const onSubmit = useCallback(
    async (body: Record<string, unknown>) => {
      if (state.kind !== 'ready') return
      try {
        const updated = (await flowDriver(ory, 'settings').update(
          state.flow.id,
          body,
        )) as AnyFlow
        // Ory answers a successful settings update with the updated flow and a
        // success message on it. Render that rather than navigating: the
        // confirmation IS the feedback.
        if (alive.current && updated?.ui !== undefined) {
          setState({ kind: 'ready', flow: updated })
        }
        // An email change can alter the identity the rail shows.
        await refresh()
      } catch (err) {
        const stepUp = await stepUpRequired(err)
        if (stepUp !== null) {
          // CARRY THE FLOW ID BACK. Without it the return journey starts a new
          // settings flow, mints a new TOTP secret, and silently invalidates
          // the QR the customer has already scanned — which is the whole of
          // #1025's second half.
          navigate('/sign-in', {
            replace: true,
            state: { from: `/settings?flow=${encodeURIComponent(state.flow.id)}`, stepUp },
          })
          return
        }
        throw err // FlowForm handles validation failures and the 400+next-flow path
      }
    },
    [navigate, ory, refresh, state],
  )

  return (
    <section className="settings">
      <header className="settings__head">
        <h1 className="settings__title">Settings</h1>
        <p className="settings__lede">
          Your sign-in details. Changing a password may ask you to sign in again first — that is
          deliberate.
        </p>
      </header>

      {state.kind === 'starting' && <p className="settings__muted">Loading…</p>}

      {state.kind === 'unavailable' && (
        <div role="alert">
          <p className="settings__error">The identity provider is unreachable.</p>
          <p className="settings__muted">{state.reason}</p>
          <button type="button" className="settings__retry" onClick={() => void start()}>
            Try again
          </button>
        </div>
      )}

      {state.kind === 'ready' && (
        <div className="settings__panel">
          <FlowForm
            flow={state.flow}
            onSubmit={onSubmit}
            onContinue={(next) => setState({ kind: 'ready', flow: next })}
            onRedirect={(to) => {
              window.location.assign(to)
            }}
          />
        </div>
      )}

      <p className="settings__foot">
        Passwords and second factors are held by Ory. Tesora never sees them.
      </p>

      {/*
        Privacy sits here rather than in the rail (#118). The rail carries the
        design's seven destinations in a fixed order (CLAUDE.md §6) and this
        issue does not get to add an eighth — and Settings is where somebody
        looks for what they have agreed to.

        Below the Ory panel, and outside it: this reads our own backend, and a
        failure of one must not take the other with it. The identity provider
        being unreachable does not stop us saying what has been asked of you.
      */}
      {/*
        APPEARANCE MOVED OUT OF THE RAIL.

        It is the design document's control and it stays exactly that — theme,
        accent and display font, the three tweaks §6 describes, unchanged. What
        moved is only where it is mounted.

        The rail is seven destinations and the person signed in. A settings
        panel wedged under them made the rail carry two different kinds of
        thing, and it is the one place in the app a customer scrolls past on
        every screen. Settings is where somebody looks to change how something
        behaves, which is what this does.
      */}
      <section className="settings__appearance" aria-label="Appearance">
        <h2 className="settings__sectionTitle">Appearance</h2>
        <p className="settings__muted">
          How Tesora looks on this device. Kept in this browser, not on your account.
        </p>
        <Appearance />
      </section>

      <PrivacyConsents />
    </section>
  )
}
